/**
 * Where Rowted's keys live — `docs/rowted-admin-v2.md` §5.1, board 2.
 *
 * A card per vendor, in the board's order, each saying the same four things:
 * what it powers, whether it works, which names are set and where each came
 * from, and what the last test found. The fourth is the one that makes this
 * screen worth having — "Connected" is a claim, "geocoded an address in 140 ms
 * two minutes ago" is evidence.
 *
 * **Nothing here has ever seen a secret.** `GET /integrations` returns four
 * characters and a source; the mask is built from `last4` rather than from a
 * value that was fetched and then hidden, which is the difference between a
 * page that cannot leak a key and a page that merely does not display one.
 */
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ApiError } from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import { Button, Card, Tag, type TagTone } from '../../components/primitives.js'
import { PageBar } from '../../frame/page-bar.js'
import { formatRelative } from '../../lib/format.js'
import { cn } from '../../lib/cn.js'
import {
  IconCheck, IconClose, IconEmail, IconExternal, IconIntegrations, IconLock, IconMaps, IconPlus,
  IconSend, IconSms, IconSparkles, IconSpinner, IconUpload, IconWarn,
} from '../../frame/icons.js'
import { AdminPage, Loading, can, useAdminUser } from './shell.js'
import { useStepUp } from './step-up.js'
import { IntegrationFlyout, refusedLine } from './integration-flyout.js'

// ---------------------------------------------------------------------------
// What the server answers with
// ---------------------------------------------------------------------------

export type IntegrationName =
  'maps' | 'graph' | 'postmark' | 'twilio' | 'stripe' | 'azure_openai' | 'storage' | 'qbo'

export interface IntegrationVar {
  name: string
  label: string
  secret: boolean
  required: boolean
  modes: boolean
  connected: boolean
  /** Made by Rowted rather than typed — Postmark's webhook password. */
  generated: boolean
  help?: string
  /** `default`: nobody set it, and it means what Rowted says it does (`MAIL_DOMAIN`). */
  source: 'env' | 'vault' | 'default' | 'missing'
  last4?: string
  setByName?: string | null
  setAt?: string
  value?: string
}

export interface IntegrationCard {
  integration: IntegrationName
  label: string
  powers: string
  connect: 'graph' | null
  /**
   * Where Microsoft has to be told to send the browser back.
   *
   * Only on the card that needs one. It is a fact about this deployment
   * rather than a secret — the app registration will not work without it,
   * and until it was on the card the one way to learn it was to read
   * AADSTS500113 off Microsoft's own error page.
   */
  redirectUri?: string
  /** Postmark's: where it posts a reply and a delivery event — never with the password in them. */
  webhooks?: { inbound: string; events: string }
  /** Postmark's: the three records to add at the DNS host, as last read. */
  dns?: DnsRecord[]
  dnsNote?: string | null
  dnsCheckedAt?: string | null
  /** Postmark's switch: whether Rowted's email goes through it now. */
  sends?: boolean
  registeredAt?: string | null
  /**
   * Postmark's latest refused webhook call, while none has got through since
   * (Aaron, Oct 26: "it gives me a http 401"). Never where it came from.
   */
  refused?: RefusedCall | null
  modes: boolean
  status: 'connected' | 'attention' | 'off' | 'test'
  mode: 'live' | 'test'
  state: Record<string, unknown>
  lastTest: { at: string; ok: boolean | null; note: string | null } | null
  vars: IntegrationVar[]
}

/** A refused call to one of Postmark's webhooks (`lastRefusedCall` in `services/postmark.ts`). */
export interface RefusedCall {
  at: string
  hook: 'events' | 'inbound' | null
  /** What to do, in the server's words. */
  sentence: string
  /** Refused since the last call that got through. */
  times: number
}

/** One of the records Postmark needs at the DNS host (`services/postmark.ts`). */
export interface DnsRecord {
  purpose: 'inbound' | 'dkim' | 'return_path' | 'dmarc'
  type: 'MX' | 'TXT' | 'CNAME'
  host: string
  value: string
  verified: boolean | null
}

/** Board 2's order, which is roughly "how often somebody touches it" — Postmark beside the mailbox it replaces. */
const ORDER: IntegrationName[] = [
  'maps', 'graph', 'postmark', 'twilio', 'stripe', 'azure_openai', 'storage', 'qbo',
]

/** The 40px tinted tile, one per vendor, from §3.1's pairs. */
const LOOK: Record<IntegrationName, { icon: typeof IconMaps; fg: string; bg: string }> = {
  maps: { icon: IconMaps, fg: 'var(--color-info)', bg: 'var(--color-info-bg)' },
  graph: {
    /* `IconEmail` is the envelope; `IconMail` is a parcel in a van. */
    icon: IconEmail,
    fg: 'rgb(var(--status-job-quoted-fg))', bg: 'rgb(var(--status-job-quoted-bg))',
  },
  postmark: {
    icon: IconSend,
    fg: 'rgb(var(--status-job-reviewed-fg))', bg: 'rgb(var(--status-job-reviewed-bg))',
  },
  twilio: {
    icon: IconSms,
    fg: 'rgb(var(--status-job-approved-fg))', bg: 'rgb(var(--status-job-approved-bg))',
  },
  stripe: {
    icon: IconLock,
    fg: 'rgb(var(--status-job-invoiced-fg))', bg: 'rgb(var(--status-job-invoiced-bg))',
  },
  azure_openai: { icon: IconSparkles, fg: 'var(--color-warn)', bg: 'var(--color-warn-bg)' },
  storage: {
    icon: IconUpload,
    fg: 'rgb(var(--status-job-scheduled-fg))', bg: 'rgb(var(--status-job-scheduled-bg))',
  },
  qbo: {
    icon: IconIntegrations,
    fg: 'rgb(var(--status-job-paid-fg))', bg: 'rgb(var(--status-job-paid-bg))',
  },
}

const STATUS_WORDS: Record<IntegrationCard['status'], { label: string; tone: TagTone }> = {
  connected: { label: 'Connected', tone: 'ok' },
  attention: { label: 'Needs attention', tone: 'warn' },
  off: { label: 'Not set up', tone: 'quiet' },
  test: { label: 'Test mode', tone: 'info' },
}

// ---------------------------------------------------------------------------

export function AdminIntegrations() {
  const { me, ready } = useAdminUser()
  const stepUp = useStepUp()
  const [params, setParams] = useSearchParams()

  const [items, setItems] = useState<IntegrationCard[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<IntegrationName | null>(null)
  const [testing, setTesting] = useState<Set<string>>(new Set())
  const [connected, setConnected] = useState<ConnectOutcome | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const got = await adminRequest<{ items: IntegrationCard[] }>('/integrations')
      const order = new Map(ORDER.map((name, index) => [name, index]))
      setItems([...got.items].sort((a, b) =>
        (order.get(a.integration) ?? 99) - (order.get(b.integration) ?? 99)))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not load.')
    }
  }, [])

  useEffect(() => { if (ready && me) void load() }, [ready, me, load])

  /**
   * `?connected=graph` — where the Microsoft round trip comes back to (§2.4).
   *
   * Two windows, because the connect dialog opens Microsoft in its own: this
   * page is what the callback redirects to, so it can land either here or in
   * that 520px popup. In the popup it is not a page anybody wants to read — it
   * hands the outcome to the window that opened it and closes. In this window
   * it says what happened and reloads the cards.
   *
   * `window.opener` is same-origin either way (the callback is our own route),
   * and the message is still origin-checked on the way in, because a listener
   * that trusts any sender is a listener any page can drive.
   */
  useEffect(() => {
    const connected = params.get('connected')
    if (!connected) return

    const outcome: ConnectOutcome = {
      failed: params.get('failed'),
      mailbox: params.get('mailbox'),
      tested: params.get('tested'),
    }

    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ rowted: 'integration-connected', outcome },
        window.location.origin)
      window.close()
      return
    }

    const copy = new URLSearchParams(params)
    for (const key of ['connected', 'failed', 'mailbox', 'tested']) copy.delete(key)
    setParams(copy, { replace: true })
    setConnected(outcome)
    void load()
  }, [params, setParams, load])

  /* The same outcome, arriving from the popup rather than from our own URL. */
  useEffect(() => {
    const listen = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      const said = event.data as { rowted?: string; outcome?: ConnectOutcome }
      if (said?.rowted !== 'integration-connected' || !said.outcome) return
      setConnected(said.outcome)
      void load()
    }
    window.addEventListener('message', listen)
    return () => window.removeEventListener('message', listen)
  }, [load])

  const runTest = useCallback(async (name: IntegrationName | 'all') => {
    setTesting((was) => new Set(was).add(name))
    setError(null)
    try {
      await adminRequest(name === 'all' ? '/integrations/test-all' : `/integrations/${name}/test`,
        { method: 'POST', body: {} })
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The test could not run.')
    } finally {
      setTesting((was) => {
        const next = new Set(was)
        next.delete(name)
        return next
      })
    }
  }, [load])

  if (!ready || !items) {
    return <AdminPage me={me} active="integrations">{error
      ? <p role="alert" className="m-0 p-4 text-body text-danger">{error}</p>
      : <Loading />}</AdminPage>
  }

  const counts = {
    connected: items.filter((one) => one.status === 'connected' || one.status === 'test').length,
    attention: items.filter((one) => one.status === 'attention').length,
    off: items.filter((one) => one.status === 'off').length,
  }

  /* "7 services · 5 connected · 1 needs attention · 1 not set up" (board 2). */
  const sub = [
    `${items.length} services`,
    counts.connected > 0 ? `${counts.connected} connected` : null,
    counts.attention > 0 ? `${counts.attention} needs attention` : null,
    counts.off > 0 ? `${counts.off} not set up` : null,
  ].filter(Boolean).join(' · ')

  const mayEdit = can(me, 'integrations')
  const mayTest = can(me, 'settings')

  return (
    <AdminPage me={me} active="integrations">
      <PageBar
        title="Integrations"
        sub={sub}
        variant="page"
        actions={mayTest
          ? (
            <Button onClick={() => void runTest('all')} disabled={testing.has('all')}>
              {testing.has('all') ? <IconSpinner size={15} /> : <IconCheck size={15} />}
              Run all tests
            </Button>
          )
          : null}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-6 pb-6 pt-4">
        {error && (
          <p role="alert" className="m-0 rounded-card bg-danger-bg px-3 py-2 text-cell
                                     text-danger">
            {error}
          </p>
        )}

        {connected && <ConnectBand outcome={connected} onClose={() => setConnected(null)} />}

        <VaultBand />

        {/*
          * `items-start`, so a card is as tall as its own well. Stretched to
          * match its neighbour, a one-key card like Maps is mostly empty space
          * with its buttons pushed to the floor of somebody else's card.
          */}
        <div className="grid items-start gap-3.5 xl:grid-cols-2">
          {items.map((card) => (
            <IntegrationCardView
              key={card.integration}
              card={card}
              mayEdit={mayEdit}
              mayTest={mayTest}
              testing={testing.has(card.integration)}
              onTest={() => void runTest(card.integration)}
              onEdit={() => setEditing(card.integration)}
            />
          ))}
          <AddPlaceholder />
        </div>
      </div>

      {editing && (
        <IntegrationFlyout
          card={items.find((one) => one.integration === editing)!}
          stepUp={stepUp}
          meEmail={me?.email ?? ''}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load() }}
        />
      )}
      {stepUp.dialog}
    </AdminPage>
  )
}

/**
 * The green band at the top — board 2's own copy.
 *
 * It is here because the two rules it states are the ones somebody about to
 * paste a key most needs to know, and neither is guessable from the screen: an
 * environment variable wins, and a change asks for a code and is written down.
 */
const VaultBand = () => (
  <div className="rounded-card border border-accent-line bg-accent-soft px-4 py-3">
    <b className="text-body text-accent-dark">Keys live in the vault, not in the code</b>
    <p className="m-0 mt-1 text-cell leading-[1.45] text-text-body">
      Every secret here is encrypted at rest with the platform key and never leaves this
      page unmasked. An environment variable still wins if it is set, so a deploy can
      override a key without touching the vault. Changes need a fresh sign-in code and are
      written to the audit log.
    </p>
  </div>
)

/**
 * What the Microsoft round trip came back saying — §5.2.
 *
 * Its own band rather than the error paragraph above, because three different
 * things can come back and only one of them is an error: it worked, it worked
 * but the test message did not send, or it did not work. A screen that reported
 * all three the same way would have somebody re-connecting a mailbox that is
 * already connected.
 */
interface ConnectOutcome {
  failed: string | null
  mailbox: string | null
  tested: string | null
}

function ConnectBand({ outcome, onClose }: {
  outcome: ConnectOutcome
  onClose: () => void
}) {
  const bad = Boolean(outcome.failed)
  return (
    <div
      role="status"
      className={cn('flex items-start gap-2 rounded-card px-4 py-3 text-cell',
        bad ? 'border border-danger-ring bg-danger-bg' : 'border border-accent-line bg-accent-soft')}
    >
      <span className={cn('mt-px shrink-0', bad ? 'text-danger' : 'text-accent-dark')}>
        {bad ? <IconWarn size={15} /> : <IconCheck size={15} />}
      </span>
      <span className={cn('min-w-0 leading-[1.45]', bad ? 'text-danger' : 'text-accent-dark')}>
        {bad
          ? <>The mailbox did not connect. {outcome.failed}</>
          : (
            <>
              <b>{outcome.mailbox ?? 'The mailbox'}</b> is now the sending mailbox.
              {outcome.tested ? ` ${outcome.tested}` : ''}
            </>
          )}
      </span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="ml-auto shrink-0 rounded-field p-0.5 text-text-muted hover:text-text"
      >
        <IconClose size={14} />
      </button>
    </div>
  )
}

const AddPlaceholder = () => (
  <div className="flex min-h-[120px] flex-col items-center justify-center gap-1 rounded-card
                  border border-dashed border-line-strong p-4 text-center">
    <span className="text-text-faint"><IconPlus size={18} /></span>
    <b className="text-body text-text-muted">Add an integration</b>
    <span className="text-cell text-text-faint">
      Weather, SSO, a second SMS provider — each one is a few lines in
      <span className="font-mono"> INTEGRATIONS</span>.
    </span>
  </div>
)

// ---------------------------------------------------------------------------
// One card
// ---------------------------------------------------------------------------

function IntegrationCardView({ card, mayEdit, mayTest, testing, onTest, onEdit }: {
  card: IntegrationCard
  mayEdit: boolean
  mayTest: boolean
  testing: boolean
  onTest: () => void
  onEdit: () => void
}) {
  const look = LOOK[card.integration]
  const Icon = look.icon
  const words = STATUS_WORDS[card.status]
  const failed = card.lastTest?.ok === false

  return (
    <Card className="flex flex-col p-0">
      <div className="flex items-start gap-3 px-[18px] pb-3 pt-4">
        <span
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-tile"
          style={{ background: look.bg, color: look.fg }}
        >
          <Icon size={18} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <b className="text-card">{card.label}</b>
            <Tag tone={words.tone} className="h-[19px] text-[11px] font-semibold">
              {words.label}
            </Tag>
            {card.modes && card.mode === 'test' && card.status !== 'test' && (
              <Tag tone="info" className="h-[19px] text-[11px] font-semibold">Test keys</Tag>
            )}
            {card.sends && (
              <Tag tone="info" className="h-[19px] text-[11px] font-semibold">Sends Rowted’s email</Tag>
            )}
          </div>
          <span className="text-cell leading-[1.45] text-text-muted">{card.powers}</span>
        </div>
      </div>

      <div className="mx-[18px] mb-3 overflow-hidden rounded-field bg-surface-alt">
        {card.vars.map((variable) => (
          <VarRowView key={variable.name} variable={variable} />
        ))}
        {card.redirectUri && <RedirectRow uri={card.redirectUri} />}
        {card.webhooks && <RedirectRow label="Inbound webhook" uri={card.webhooks.inbound} />}
        {card.webhooks && <RedirectRow label="Events webhook" uri={card.webhooks.events} />}
        {card.dns && <DnsSummaryRow records={card.dns} />}
      </div>

      {card.refused && (
        <p role="status" data-refused
          className="mx-[18px] mb-3 m-0 flex items-start gap-2 rounded-field bg-danger-bg px-3
                     py-2 text-cell leading-[1.45] text-danger">
          <span className="mt-px shrink-0"><IconWarn size={14} /></span>
          <span>{refusedLine(card.refused)}</span>
        </p>
      )}

      {failed && (
        <p className="mx-[18px] mb-3 m-0 flex items-start gap-2 rounded-field bg-danger-bg px-3
                      py-2 text-cell text-danger">
          <span className="mt-px shrink-0"><IconWarn size={14} /></span>
          {card.lastTest?.note}
        </p>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-line-soft
                      px-[18px] py-3">
        <span className="min-w-0 flex-1 truncate text-[11.5px] text-text-faint">
          {card.lastTest
            ? `Last test ${formatRelative(card.lastTest.at)}${
              card.lastTest.ok ? ` · ${card.lastTest.note ?? ''}` : ''}`
            : 'Never tested.'}
        </span>

        {card.status === 'off' && mayEdit
          ? <Button variant="primary" onClick={onEdit}>Set up</Button>
          : (
            <>
              {mayTest && (
                <Button onClick={onTest} disabled={testing}>
                  {testing ? <IconSpinner size={15} /> : <IconCheck size={15} />}
                  Test
                </Button>
              )}
              {card.integration === 'stripe' && (
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-9 items-center gap-1.5 rounded-button border
                             border-line bg-surface px-3 text-cell font-medium text-text
                             hover:border-line-strong"
                >
                  <IconExternal size={15} />
                  Open Stripe
                </a>
              )}
              {mayEdit && <Button onClick={onEdit}>Edit keys</Button>}
            </>
          )}
      </div>
    </Card>
  )
}

/**
 * The address the vendor has to be told about — the last row of the well.
 *
 * In the well with the keys rather than beside the Connect button, because it
 * is the same kind of thing: something that has to match on both sides or
 * nothing works. It is read-only and copyable and never editable here — the
 * value is `APP_URL` plus a fixed path, so a box somebody could type in would
 * be a box that lies about what the server will actually send.
 */
function RedirectRow({ uri, label = 'Redirect URI' }: { uri: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  return (
    <div className="flex items-center gap-3 border-t border-line-soft px-3 py-2.5 first:border-t-0">
      <span className="w-[150px] shrink-0 text-cell text-text-muted">{label}</span>
      <code className="min-w-0 flex-1 truncate text-[12px] text-text" title={uri}>{uri}</code>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(uri)
            .then(() => setCopied(true))
            /* A browser that will not hand over the clipboard without a
               gesture it trusts. The address is on screen and selectable,
               which is the fallback. */
            .catch(() => setCopied(false))
        }}
        className="shrink-0 text-cell font-semibold text-accent hover:text-accent-dark"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

/** The records Postmark's mail domain needs: MX, DKIM, Return-Path and DMARC (Aaron, Oct 27, answer 18). */
const DNS_RECORDS = 4

/**
 * Postmark's four DNS records, in one line of the well: how many are
 * verified. The records themselves, with their values to copy, are in the
 * flyout — a TXT value is 200 characters and does not belong on a card.
 */
function DnsSummaryRow({ records }: { records: DnsRecord[] }) {
  const verified = records.filter((one) => one.verified === true).length
  const unknown = records.length < DNS_RECORDS || records.some((one) => one.verified === null)
  return (
    <div className="flex items-center gap-3 border-t border-line-soft px-3 py-2.5 first:border-t-0 text-cell">
      <span className="w-[150px] shrink-0 text-text-muted">DNS records</span>
      <span className={cn('min-w-0 flex-1 truncate', verified === DNS_RECORDS ? 'text-text' : 'text-text-muted')}>
        {verified === DNS_RECORDS ? 'All four verified' : `${verified} of ${DNS_RECORDS} verified`}
      </span>
      <span className="shrink-0 text-[11px] text-text-faint">
        {unknown ? 'not all checked yet' : 'in Edit keys'}
      </span>
    </div>
  )
}

/**
 * One name, in the well.
 *
 * The mask is built from `last4` and nothing else, so what is on screen is
 * literally all the page was given. `••••` with the real length would leak the
 * length; a fixed run of dots does not.
 */
function VarRowView({ variable }: { variable: IntegrationVar }) {
  const shown = variable.secret
    ? (variable.last4 ? `••••••••••••${variable.last4}` : null)
    : variable.value ?? null

  return (
    <div className="grid grid-cols-[minmax(110px,150px)_minmax(0,1fr)] items-baseline gap-3
                    border-b border-line-soft px-3 py-2 last:border-b-0 text-cell">
      {/*
        * No marker beside the label: the note on the right already says
        * "required" or "optional" for a name that is not set, and a dot that
        * means the same thing a word means is a dot that reads as a typo.
        */}
      <span className="truncate text-text-muted">{variable.label}</span>
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className={cn('truncate', shown ? 'font-mono text-text' : 'text-text-faint')}>
          {shown ?? (variable.connected ? 'Not connected' : variable.generated ? 'Not made yet' : 'Not set')}
        </span>
        <span className="text-[11px] text-text-faint">{note(variable)}</span>
      </span>
    </div>
  )
}

/** The muted half-line after a value: where it came from, and who put it there. */
function note(variable: IntegrationVar): string {
  if (variable.source === 'env') return 'from environment'
  if (variable.source === 'default') return 'default'
  if (variable.source === 'missing') return variable.required ? 'required' : 'optional'
  return [
    variable.setAt ? `set ${formatRelative(variable.setAt)}` : null,
    variable.setByName,
  ].filter(Boolean).join(' · ')
}
